import { useQuery } from '@tanstack/react-query'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import Header from '@components/header'
import Layout from '@components/Layout'
import CancelSaleModal from '@components/Modal/cancelSaleModal'
import OrderPaymentModal from '@components/Modal/orderPaymentModal'
import ViewSalesModal from '@components/Modal/ViewSalesModal'
import { columnSalesOrderNotAdmin } from '@utils/columns'
import { debouncedSet } from '@utils/debounce'
import { fetchSalesOrder } from '@utils/fetchData'
import {
	handleNavigate,
	handleNavigateCar,
	handleNavigateSeries,
	handleNavigateMiniDashboard,
} from '@utils/navigateToPdf'
import { changePageSize, changePagination } from '@utils/pagination'
import { getToday } from '@utils/time'

const SalesOrder = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()


	const { department, roleNames } = useSelector((state) => state.main)

	const formattedDate = getToday()

	const [currentPage, setCurrentPage] = useState(0)
	const [isModalVisible, setIsModalVisible] = useState(false)
	const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false)

	const [isCancelModalVisible, setIsCancelModalVisible] = useState(false)
	const [selectedRowData, setSelectedRowData] = useState(null)
	const [docNum, setDocNum] = useState('')
	const [cardName, setCardName] = useState('')
	const [slpName, setSlpName] = useState('')
	const [startDate, setStartDate] = useState('')
	const [endDate, setEndDate] = useState(formattedDate)
	const [cancelStatus, setCancelStatus] = useState('-')
	const [delStatus, setDelStatus] = useState('')
	const [orderStatus, setOrderStatus] = useState('')
	const [pageSize, setPageSize] = useState('10')

	const {
		data = [],
		isLoading,
		refetch,
	} = useQuery({
		queryKey: [
			'salesOrder',
			currentPage,
			cardName,
			docNum,
			startDate,
			endDate,
			slpName,
			cancelStatus,
			delStatus,
			pageSize,
			roleNames.includes('Supplier') ||
			roleNames.includes('admin')
				? ''
				: department,
			orderStatus,
		],
		queryFn: fetchSalesOrder,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	const handleRowClick = (record) => {
		setSelectedRowData(record)
		setIsModalVisible(true)
	}

	const handleCancelClick = (record) => {
		setSelectedRowData(record)
		setIsCancelModalVisible(true)
	}

	const handleStartDate = (e) => {
		setStartDate(e.target.value)
	}

	const handleEndDate = (e) => {
		setEndDate(e.target.value)
	}

	const handleDocNumChange = (val) => {
		debouncedSet(val, setDocNum)
	}

	const handleCardNameChange = (val) => {
		debouncedSet(val, setCardName)
	}

	const handleSlpNameChange = (val) => {
		debouncedSet(val, setSlpName)
	}

	const handleOpenPaymentModal = (record) => {
		setSelectedRowData(record)
		setIsPaymentModalVisible(true)
	}

	const onClickEdit = (record) => {
		navigate(`${record.docEntry}`)
	}

	const columns = columnSalesOrderNotAdmin(
		t,
		handleRowClick,
		handleDocNumChange,
		handleCardNameChange,
		handleSlpNameChange,
		handleNavigate,
		handleNavigateCar,
		handleNavigateMiniDashboard,
		handleCancelClick,
		handleNavigateSeries,
		roleNames.includes('admin') || roleNames.includes('Sales Manager')
			? true
			: false,
		roleNames.includes('Cashier') || roleNames.includes('admin')
			? true
			: false,
		handleOpenPaymentModal,
		roleNames.includes('admin') || roleNames.includes('Sales Manager')
			? true
			: false,
		onClickEdit,
	)

	const handleAdd = () => {
		navigate('add')
	}

	const handleAddCopy = () => {
		navigate('addCopy')
	}

	const switchRowBg = (orderStatus) => {
		switch (orderStatus) {
			case "To'lanmasdan boshlangan":
				return 'bg-orange-300'
			case "To'lanmasdan tayyor qilib olib chiqib ketilgan":
				return 'bg-red-300'
			case "To'lanmasdan tayyor qilib qo'yilgan":
				return 'bg-sky-300'
			case 'Zaklad berib boshlangan':
				return 'bg-gray-300'
			default:
				return 'bg-white'
		}
	}

	const handleOrderStatusFilter = () => {
		return [
			{
				id: 1,
				className: 'bg-orange-300',
				meaning: "To'lanmasdan boshlangan",
				key: "To'lanmasdan boshlangan",
			},
			{
				id: 2,
				className: 'bg-red-300',
				meaning: "To'lanmasdan tayyor qilib olib chiqib ketilgan",
				key: "To'lanmasdan tayyor qilib olib chiqib ketilgan",
			},
			{
				id: 3,
				className: 'bg-sky-300',
				meaning: "To'lanmasdan tayyor qilib qo'yilgan",
				key: "To'lanmasdan tayyor qilib qo'yilgan",
			},
			{
				id: 4,
				className: 'bg-gray-300',
				meaning: 'Zaklad berib boshlangan',
				key: 'Zaklad berib boshlangan',
			},
			{
				id: 5,
				className: 'bg-gray-100',
				meaning: "To'lov kutilmoqda",
				key: "To'lov kutilmoqda",
			},

			{
				id: 5,
				className: 'bg-black',
				meaning: t('showAll'),
				key: '',
			},
		]
	}

	const orderStatusFilter = handleOrderStatusFilter()

	const handlePaginationChange = (page, pageSize) => {
		changePagination({
			page,
			pageSize,
			setCurrentPage,
			setPageSize,
		})
	}
	const handlePageSizeChange = (current, pageSize) => {
		changePageSize({
			current,
			pageSize,
			setCurrentPage,
			setPageSize,
		})
	}

	return (
		<Layout>
			<Header
				title={
					roleNames.includes('admin') ? t('orders') : t('SalesOrder')
				}
				columns={columns}
				fdata={data}
				loading={isLoading}
				onStartDateChange={handleStartDate}
				onEndDateChange={handleEndDate}
				startDate={startDate}
				endDate={endDate}
				isSticky={false}
				formattedDate={formattedDate}
				isShowPagination
				handlePageSizeChange={handlePageSizeChange}
				handlePaginationChange={handlePaginationChange}
				tableProps={{
					rowClassName: (record) => {
						return switchRowBg(record?.u_OrderStatus)
					},
				}}
				// renderFiltersInBottom={() => {
				// 	return (
				// 		<div className="flex gap-3 flex-wrap">
				// 			{orderStatusFilter.map((item, index) => (
				// 				<div
				// 					key={index}
				// 					className="flex gap-1 items-center cursor-pointer"
				// 					onClick={() => {
				// 						setOrderStatus(item.key)
				// 						setCurrentPage(0)
				// 					}}
				// 				>
				// 					<div
				// 						className={`rounded-full ${item.className} w-[20px] h-[20px]`}
				// 					></div>
				// 					<span>{item.meaning}</span>
				// 				</div>
				// 			))}
				// 		</div>
				// 	)
				// }}
				{...(roleNames.includes('Sales Manager') ||
				roleNames.includes('admin')
					? { onBtnClick: handleAdd }
					: {})}
			/>
			<ViewSalesModal
				visible={isModalVisible}
				data={selectedRowData}
				onClose={() => setIsModalVisible(false)}
				ship={true}
				incoming={true}
				refetch={refetch}
			/>
			<CancelSaleModal
				visible={isCancelModalVisible}
				data={selectedRowData}
				onClose={() => {
					setIsCancelModalVisible(false)
					refetch()
				}}
			/>
			<OrderPaymentModal
				visible={isPaymentModalVisible}
				data={selectedRowData}
				onClose={() => setIsPaymentModalVisible(false)}
			/>
		</Layout>
	)
}

export default SalesOrder


